import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, Gift, AlertTriangle, FileText, Sparkles, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_META = {
  birthday:         { label: 'Aniversário', color: 'text-pink-400',   dot: 'bg-pink-500',   Icon: Gift },
  project_deadline: { label: 'Entrega',     color: 'text-red-400',    dot: 'bg-red-500',    Icon: AlertTriangle },
  demo_deadline:    { label: 'Demo',        color: 'text-orange-400', dot: 'bg-orange-500', Icon: FileText },
  project_created:  { label: 'Criação',     color: 'text-green-400',  dot: 'bg-green-500',  Icon: Sparkles },
  invoice_pending:  { label: 'Retake',      color: 'text-yellow-400', dot: 'bg-yellow-500', Icon: AlertCircle },
};

const getMeta = (type) =>
  EVENT_META[type] || { label: 'Evento', color: 'text-blue-400', dot: 'bg-blue-500', Icon: CalendarIcon };

const formatDayLabel = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.getTime() === today.getTime()) return 'Hoje';
  if (d.getTime() === tomorrow.getTime()) return 'Amanhã';
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${weekday} ${day}/${month}`;
};

/**
 * Agenda da Semana — mostra eventos dos próximos 7 dias (hoje inclusive).
 * Reaproveita o endpoint /api/calendar/events.
 */
export const UpcomingWeekWidget = ({ getAuthHeaders }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/calendar/events`, { headers: getAuthHeaders() })
      .then(res => {
        if (cancelled) return;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const horizon = new Date(today); horizon.setDate(today.getDate() + 7);
        const filtered = (res.data || [])
          .filter(e => {
            if (!e?.date) return false;
            const d = new Date(e.date + 'T00:00:00');
            return d >= today && d < horizon;
          })
          .sort((a, b) => a.date.localeCompare(b.date));
        setEvents(filtered);
      })
      .catch(() => { /* silent — widget is a dashboard extra */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700" data-testid="upcoming-week-widget">
      <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
        <CalendarIcon className="h-5 w-5 text-purple-400" />
        Próximos 7 dias
        {!loading && events.length > 0 && (
          <span className="text-xs font-normal text-slate-400 ml-1">({events.length})</span>
        )}
      </h2>

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-purple-400"></div>
        </div>
      ) : events.length === 0 ? (
        <p className="text-slate-400 text-sm">Nenhum evento nos próximos 7 dias.</p>
      ) : (
        <ul className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {events.map((e, idx) => {
            const { label, color, dot, Icon } = getMeta(e.type);
            return (
              <li
                key={`${e.date}-${e.type}-${e.title}-${idx}`}
                className="flex items-start gap-3 bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2"
                data-testid={`upcoming-evt-${idx}`}
              >
                <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`${color} font-medium flex items-center gap-1`}>
                      <Icon className="h-3 w-3" />
                      {label}
                    </span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-400">{formatDayLabel(e.date)}</span>
                  </div>
                  <p className="text-sm text-white truncate mt-0.5">{e.title}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
